import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GoogleConsentService } from './google-consent.service';

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-api-script';
const MARKER_CLUSTERER_SCRIPT_ID = 'markerclusterer-script';
const MARKER_CLUSTERER_URL = 'https://unpkg.com/@googlemaps/markerclustererplus/dist/index.min.js';

@Injectable({
    providedIn: 'root',
})
export class GoogleMapsLoaderService {
    private readonly googleConsentService = inject(GoogleConsentService);
    private mapsLoadPromise: Promise<void> | null = null;

    loadIfAllowed(): void {
        void this.ensureLoaded().catch(() => undefined);
    }

    isApiReady(): boolean {
        return typeof window.google !== 'undefined' && !!window.google.maps;
    }

    ensureLoaded(): Promise<void> {
        if (!this.googleConsentService.hasFunctionalityConsent()) {
            return Promise.reject(new Error('Functionality consent not granted'));
        }

        if (this.isApiReady()) {
            return Promise.resolve();
        }

        if (!this.mapsLoadPromise) {
            this.mapsLoadPromise = this.loadGoogleMapsApiAsync()
                .then(() => this.loadMarkerClustererAsync())
                .catch(error => {
                    this.mapsLoadPromise = null;
                    throw error;
                });
        }

        return this.mapsLoadPromise;
    }

    private loadGoogleMapsApiAsync(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isApiReady()) {
                resolve();
                return;
            }

            const existingScript = document.getElementById(
                GOOGLE_MAPS_SCRIPT_ID
            ) as HTMLScriptElement | null;

            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(), { once: true });
                existingScript.addEventListener('error', () => reject(), { once: true });
                return;
            }

            const script = document.createElement('script');
            script.id = GOOGLE_MAPS_SCRIPT_ID;
            script.async = true;
            script.defer = true;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.assets.googleMaps.key}`;
            script.addEventListener('load', () => resolve(), { once: true });
            script.addEventListener('error', () => reject(), { once: true });
            document.head.appendChild(script);
        });
    }

    private loadMarkerClustererAsync(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (document.getElementById(MARKER_CLUSTERER_SCRIPT_ID)) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.id = MARKER_CLUSTERER_SCRIPT_ID;
            script.async = true;
            script.src = MARKER_CLUSTERER_URL;
            script.addEventListener('load', () => resolve(), { once: true });
            script.addEventListener('error', () => reject(), { once: true });
            document.head.appendChild(script);
        });
    }
}
